import { useEffect, useRef, useState } from 'react'
import PecasAcervo from '@services/pecasAcervo'
import { indexBatch, needsIndex } from './acervoBatch'

export default function useAcervoBulkIndex(onChanged) {
  const [rows, setRows] = useState([])
  const [phase, setPhase] = useState('idle')
  const [notice, setNotice] = useState('')
  const [stopping, setStopping] = useState(false)
  const [reindexing, setReindexing] = useState(false)
  const [pendingExit, setPendingExit] = useState(null)
  const [waitingExit, setWaitingExit] = useState(false)
  const operation = useRef(null)
  const stopRequested = useRef(false)
  const exitAfterCurrent = useRef(null)
  const rowsRef = useRef([])
  const active = phase === 'loading' || phase === 'indexing'

  useEffect(() => () => { operation.current?.abort() }, [])
  useEffect(() => {
    if (!active) return undefined
    const beforeUnload = (event) => { event.preventDefault(); event.returnValue = '' }
    const beforeLogout = (event) => {
      if (typeof event.detail?.proceed !== 'function') return
      event.preventDefault()
      setPendingExit({ proceed: event.detail.proceed })
    }
    window.addEventListener('beforeunload', beforeUnload)
    window.addEventListener('auth:before-logout', beforeLogout)
    return () => {
      window.removeEventListener('beforeunload', beforeUnload)
      window.removeEventListener('auth:before-logout', beforeLogout)
    }
  }, [active])

  const stop = () => { stopRequested.current = true; setStopping(true) }
  const cancelExit = () => {
    if (waitingExit) return
    setPendingExit(null)
  }
  const confirmExit = (navigate) => {
    const proceed = navigate || pendingExit?.proceed
    if (operation.current) {
      exitAfterCurrent.current = proceed
      stop()
      setWaitingExit(true)
    } else { setPendingExit(null); proceed?.() }
  }

  const run = async ({ incluirProntos = false } = {}) => {
    if (operation.current) return
    const controller = new AbortController()
    operation.current = controller
    stopRequested.current = false
    rowsRef.current = []
    setRows([])
    setStopping(false)
    setReindexing(incluirProntos)
    setNotice('')
    setPhase('loading')
    let changed = false
    try {
      // Indexing changes UpdatedAt and therefore pagination order. Gather every
      // page before starting any mutation, ignoring the list's display filters.
      const documents = new Map()
      let page = 1
      while (!controller.signal.aborted && !stopRequested.current) {
        const result = await PecasAcervo.list({ page, pageSize: 100 }, controller.signal)
        if (controller.signal.aborted) return
        if (!result.success) throw new Error(result.error || 'Não foi possível consultar todas as peças.')
        result.data.items.forEach((document) => documents.set(document.id, document))
        if (!result.data.items.length || page * result.data.pageSize >= result.data.total) break
        page += 1
      }
      if (stopRequested.current) { setNotice('Consulta interrompida. Nenhuma indexação foi iniciada.'); return }
      const pending = [...documents.values()].filter((document) => needsIndex(document, { incluirProntos })).map((documento) => ({
        id: documento.id, documento, hasText: true, status: 'saved', error: '',
      }))
      rowsRef.current = pending
      setRows(pending)
      if (!pending.length) {
        setNotice(incluirProntos ? 'Não há peças elegíveis para reindexar. Confira o texto e a classificação dos documentos.'
          : 'Não há peças pendentes para indexar. Confira se as demais estão disponíveis ou precisam de texto e classificação.')
        return
      }
      setPhase('indexing')
      await indexBatch(pending, {
        api: PecasAcervo, signal: controller.signal, shouldStop: () => stopRequested.current, incluirProntos,
        onSaved: () => { changed = true },
        onChange: (id, patch) => {
          if (controller.signal.aborted) return
          // A document may have been edited or archived since the initial list.
          if (patch.status === 'saved' && (!patch.hasText || !patch.documento?.area || !patch.documento?.acao || patch.documento?.status === 'ARQUIVADO'))
            patch = { ...patch, status: 'skipped', error: patch.error || 'Adicione o texto e confira a classificação na peça.' }
          rowsRef.current = rowsRef.current.map((row) => row.id === id ? { ...row, ...patch } : row)
          setRows(rowsRef.current)
        },
      })
      if (!controller.signal.aborted) {
        const pendingCount = rowsRef.current.filter((row) => row.status === 'saved').length
        setNotice(stopRequested.current ? 'Lote interrompido após a operação atual. As peças concluídas continuam disponíveis.'
          : pendingCount ? 'O lote parou antes de concluir todas as peças. Confira os resultados e tente novamente as pendentes.'
            : 'Lote finalizado. Confira o resultado de cada peça abaixo.')
      }
    } catch (error) {
      if (!controller.signal.aborted) setNotice(error.message || 'Não foi possível concluir a indexação em lote.')
    } finally {
      operation.current = null
      if (!controller.signal.aborted) {
        setPhase('done')
        setStopping(false)
        setWaitingExit(false)
        if (changed) onChanged()
        const proceed = exitAfterCurrent.current
        exitAfterCurrent.current = null
        if (proceed) { setPendingExit(null); proceed() }
      }
    }
  }

  return { rows, phase, notice, stopping, reindexing, active, run, stop, waitingExit, cancelExit, confirmExit,
    exitRequested: Boolean(pendingExit) }
}
